/**
 * Allys Area Selector Component
 * 
 * Navigation component showing all regions and areas
 * for easy exploration of different neighbourhoods.
 */

'use client';

import Link from 'next/link';
import type { AreaSelectorProps, AreaPageMessages } from '@routeroyal/theme-core';

export interface AllysAreaSelectorProps extends AreaSelectorProps {
  /**
   * Translation messages
   */
  messages?: AreaPageMessages;
  
  /**
   * Current locale
   */
  locale?: string;
}

export function AllysAreaSelector({
  regions,
  currentArea,
  basePath = '/areas',
  messages,
  locale = 'en',
  className = '',
}: AllysAreaSelectorProps) {
  return (
    <div className={`bg-[var(--color-text)] text-white py-20 transition-colors duration-500 ${className}`}>
      <div className="container mx-auto px-6 md:px-12">
        <h2 className="font-serif text-3xl md:text-4xl mb-12 text-center">
          {messages?.exploreOtherRegions || 'Explore Other Regions'}
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {regions.map((region) => (
            <div key={region.region}>
              <h3 className="text-[var(--color-primary)] font-serif text-xl mb-6 border-b border-[var(--color-primary)]/20 pb-2">
                {region.region}
              </h3>
              <ul className="space-y-3">
                {region.areas.map((area) => (
                  <li key={area.slug}>
                    <Link 
                      href={`/${locale}${basePath}/${area.slug}`}
                      className={`font-inter text-sm font-light tracking-wide hover:text-[var(--color-primary)] transition-all hover:pl-2 duration-300 text-left w-full inline-block ${
                        currentArea === area.slug 
                          ? 'text-[var(--color-primary)] pl-2 font-medium' 
                          : 'text-white/70'
                      }`}
                    >
                      {area.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
